import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, Download, Loader2, Sparkles, Plus, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const ENSEIGNE_COLORS = {
  'Intermarché': '#10B981',
  'Système U':   '#8B5CF6',
  'Leclerc':     '#0055FF',
};

const formatCA = (v) => {
  if (!v) return '—';
  const m = v / 1_000_000;
  return `${m.toFixed(1)} M€`;
};

export default function SuggestionsPage() {
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({
    enseigne: 'all',
    departement: '',
    ca_min: 30,
    search: '',
  });
  const [addModal, setAddModal] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // 'existing' | 'new'

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        exclude_wasabi: 'true',
        exclude_hanagroup: 'true',
        only_localized: 'false',
      });
      if (filters.enseigne !== 'all') params.set('enseigne_group', filters.enseigne);
      if (filters.departement) params.set('departement', filters.departement);
      if (filters.ca_min) params.set('ca_min', String(filters.ca_min * 1_000_000));
      if (filters.search) params.set('search', filters.search);
      const { data } = await api.get(`/maps/targets?${params.toString()}`);
      setTargets(data.targets || []);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { api.get('/campaigns').then(r => setCampaigns(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [filters]); // eslint-disable-line

  // Reset selection when filters change (result set changes)
  useEffect(() => { setSelected(new Set()); }, [targets]);

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAllVisible = () => {
    if (selected.size >= targets.length) setSelected(new Set());
    else setSelected(new Set(targets.map(t => t.id)));
  };
  const selectedCount = selected.size;
  const selectedCA = useMemo(() =>
    targets.filter(t => selected.has(t.id)).reduce((s, t) => s + (t.ca_estime || 0), 0),
    [selected, targets]
  );

  const stats = useMemo(() => {
    const byGroup = {};
    let totalCA = 0;
    targets.forEach(t => {
      byGroup[t.enseigne_group] = (byGroup[t.enseigne_group] || 0) + 1;
      totalCA += t.ca_estime || 0;
    });
    return { byGroup, totalCA };
  }, [targets]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ only_available: 'true' });
      if (filters.enseigne !== 'all') params.set('enseigne_group', filters.enseigne);
      const resp = await api.get(`/maps/targets/export-excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `suggestions_cibles_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Export téléchargé');
    } catch { toast.error('Erreur export'); }
  };

  const resetFilters = () => setFilters({ enseigne: 'all', departement: '', ca_min: 30, search: '' });

  return (
    <div className="space-y-5 animate-fade-in" data-testid="suggestions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Sparkles className="w-6 h-6 text-[#F97316]"/>
            Suggestions prospects
          </h1>
          <p className="text-sm text-[#71717A] mt-1">Magasins cibles GMS (≥30 M€ CA) disponibles — hors clients WASABI et hors positions HANAGROUP.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={targets.length === 0} className="h-9" data-testid="suggestions-export-btn">
            <Download className="w-4 h-4 mr-1.5"/>Export Excel
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Cibles affichées" value={targets.length} color="#F97316"/>
        <Kpi label="Intermarché" value={stats.byGroup['Intermarché'] || 0} color="#10B981"/>
        <Kpi label="Système U" value={stats.byGroup['Système U'] || 0} color="#8B5CF6"/>
        <Kpi label="Leclerc" value={stats.byGroup['Leclerc'] || 0} color="#0055FF"/>
      </div>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] text-xs text-[#92400E] font-semibold">
        💰 CA cumulé : {formatCA(stats.totalCA)}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#71717A]"/>
          <span className="text-sm font-semibold">Filtres</span>
          <button onClick={resetFilters} className="ml-auto text-xs text-[#71717A] hover:text-[#F97316] flex items-center gap-1" data-testid="suggestions-reset-btn">
            <RotateCcw className="w-3 h-3"/>Réinitialiser
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-[#71717A] mb-1 block">Enseigne</label>
            <Select value={filters.enseigne} onValueChange={v => setFilters(f => ({...f, enseigne: v}))}>
              <SelectTrigger className="h-9" data-testid="suggestions-filter-enseigne"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="Intermarché">Intermarché</SelectItem>
                <SelectItem value="Système U">Système U</SelectItem>
                <SelectItem value="Leclerc">Leclerc</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#71717A] mb-1 block">Département(s)</label>
            <Input value={filters.departement} onChange={e => setFilters(f => ({...f, departement: e.target.value}))}
              placeholder="75,13,33..." className="h-9" data-testid="suggestions-filter-dep"/>
          </div>
          <div>
            <label className="text-xs text-[#71717A] mb-1 block">CA min (M€)</label>
            <Input type="number" min="0" step="5" value={filters.ca_min} onChange={e => setFilters(f => ({...f, ca_min: Number(e.target.value) || 0}))}
              className="h-9" data-testid="suggestions-filter-ca"/>
          </div>
          <div>
            <label className="text-xs text-[#71717A] mb-1 block">Recherche</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#71717A]"/>
              <Input value={filters.search} onChange={e => setFilters(f => ({...f, search: e.target.value}))}
                placeholder="Ville, magasin..." className="pl-8 h-9" data-testid="suggestions-filter-search"/>
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-400px)] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-xs text-[#71717A] uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="w-10 py-2.5 px-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <input type="checkbox"
                    checked={selectedCount > 0 && selectedCount >= targets.length}
                    ref={el => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < targets.length; }}
                    onChange={toggleAllVisible}
                    data-testid="suggestions-toggle-all"/>
                </th>
                {['Enseigne', 'Magasin', 'Adresse', 'CP', 'Ville', 'CA estimé', 'Action'].map(h => (
                  <th key={h} className="text-left py-2.5 px-3 font-medium border-b border-[#E5E7EB] bg-[#F9FAFB]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="8" className="text-center py-10 text-[#71717A]"><Loader2 className="w-5 h-5 animate-spin inline-block"/></td></tr>
              )}
              {!loading && targets.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 text-[#71717A]">Aucune cible ne correspond aux filtres</td></tr>
              )}
              {!loading && targets.map(t => (
                <tr key={t.id} className={`border-b border-[#F4F4F5] hover:bg-[#FFF7ED] ${selected.has(t.id) ? 'bg-[#FFF7ED]' : ''}`} data-testid={`suggestion-row-${t.id}`}>
                  <td className="py-2 px-3">
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleOne(t.id)}
                      data-testid={`suggestion-check-${t.id}`}/>
                  </td>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: `${ENSEIGNE_COLORS[t.enseigne_group]}15`, color: ENSEIGNE_COLORS[t.enseigne_group] }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: ENSEIGNE_COLORS[t.enseigne_group] }}/>
                      {t.enseigne_group}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs font-medium max-w-[200px] truncate" title={t.enseigne_detail}>{t.enseigne_detail}</td>
                  <td className="py-2 px-3 text-xs text-[#71717A] max-w-[240px] truncate" title={t.rue}>{t.rue}</td>
                  <td className="py-2 px-3 text-xs tabular-nums">{t.code_postal}</td>
                  <td className="py-2 px-3 text-xs">{t.commune || t.ville}</td>
                  <td className="py-2 px-3 text-xs font-semibold text-[#B45309] tabular-nums">{formatCA(t.ca_estime)}</td>
                  <td className="py-2 px-3">
                    <Button size="sm" className="h-7 text-[11px] bg-[#F97316] hover:bg-[#EA580C]"
                      onClick={() => setAddModal(t)} data-testid={`suggestion-add-${t.id}`}>
                      <Plus className="w-3 h-3 mr-0.5"/>Prospect
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {addModal && (
        <QuickAddModal target={addModal} campaigns={campaigns}
          onClose={() => setAddModal(null)}
          onSuccess={() => { setAddModal(null); load(); }}/>
      )}
      {bulkModal && (
        <BulkAddModal
          targetIds={Array.from(selected)}
          targets={targets.filter(t => selected.has(t.id))}
          campaigns={campaigns}
          initialMode={bulkModal}
          onClose={() => setBulkModal(null)}
          onSuccess={() => { setBulkModal(null); setSelected(new Set()); load(); }}
        />
      )}

      {/* Floating bulk action bar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[900] bg-[#18181B] text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 animate-slide-up" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center text-xs font-bold">{selectedCount}</span>
            <span className="text-sm font-medium">sélectionnée{selectedCount > 1 ? 's' : ''}</span>
            <span className="text-xs text-[#A1A1AA]">• CA {formatCA(selectedCA)}</span>
          </div>
          <div className="w-px h-6 bg-[#3F3F46]"/>
          <Button size="sm" className="h-8 text-xs bg-[#F97316] hover:bg-[#EA580C]" onClick={() => setBulkModal('existing')} data-testid="bulk-to-existing">
            <Plus className="w-3 h-3 mr-1"/>Campagne existante
          </Button>
          <Button size="sm" className="h-8 text-xs bg-white text-[#F97316] hover:bg-[#F4F4F5]" onClick={() => setBulkModal('new')} data-testid="bulk-to-new">
            <Sparkles className="w-3 h-3 mr-1"/>Nouvelle campagne
          </Button>
          <button onClick={() => setSelected(new Set())} className="text-[#A1A1AA] hover:text-white ml-1" data-testid="bulk-clear">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <Card className="p-3">
      <div className="text-xs text-[#71717A] mb-1">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tabular-nums" style={{ color, fontFamily: 'var(--font-heading)' }}>{value}</span>
      </div>
    </Card>
  );
}

function QuickAddModal({ target, campaigns, onClose, onSuccess }) {
  const [mode, setMode] = useState('free'); // existing | new | free
  const [campagneId, setCampagneId] = useState('');
  const [newName, setNewName] = useState(`Cibles ${target.enseigne_group} - ${target.commune || target.ville}`);
  const [concurrent, setConcurrent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {};
      if (mode === 'existing') {
        if (!campagneId) { toast.error('Sélectionnez une campagne'); setSubmitting(false); return; }
        payload.campagne_id = campagneId;
      } else if (mode === 'new') {
        payload.nouvelle_campagne_nom = newName.trim();
        payload.nouvelle_campagne_concurrent = concurrent.trim();
      }
      const { data } = await api.post(`/maps/targets/${target.id}/to-prospect`, payload);
      if (data.already_exists) toast.info('Ce prospect existe déjà');
      else toast.success('Prospect créé • 🔍 Enrichissement auto en cours');
      onSuccess(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'wasabi_client') toast.error(`🛡 ${d.message}`);
      else toast.error((typeof d === 'string' ? d : d?.message) || 'Erreur');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-1">Créer un prospect</h3>
        <p className="text-xs text-[#71717A] mb-3">{target.enseigne_detail} — {target.commune || target.ville}</p>
        <div className="bg-[#F9FAFB] rounded-lg p-3 mb-4 text-xs space-y-0.5">
          <div>{target.rue}</div>
          <div>{target.code_postal} {target.commune || target.ville}</div>
          <div className="font-semibold text-[#B45309]">💰 {formatCA(target.ca_estime)}</div>
        </div>
        <div className="grid grid-cols-3 gap-1 mb-3">
          {[['free', 'Hors campagne'], ['existing', 'Campagne existante'], ['new', 'Nouvelle campagne']].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)}
              className={`py-1.5 px-1 rounded text-[11px] font-medium border transition-colors ${mode === k ? 'bg-[#F97316] text-white border-[#F97316]' : 'bg-white text-[#71717A] border-[#E5E7EB] hover:bg-[#FFF7ED]'}`}>
              {l}
            </button>
          ))}
        </div>
        {mode === 'existing' && (
          <Select value={campagneId} onValueChange={setCampagneId}>
            <SelectTrigger className="h-9 mb-4"><SelectValue placeholder="Sélectionner..."/></SelectTrigger>
            <SelectContent>
              {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {mode === 'new' && (
          <>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nom de campagne" className="h-9 mb-2"/>
            <Input value={concurrent} onChange={e => setConcurrent(e.target.value)} placeholder="Prestataire en place (optionnel)" className="h-9 mb-4"/>
          </>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>Annuler</Button>
          <Button onClick={submit} className="flex-1 bg-[#F97316] hover:bg-[#EA580C]" disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Créer'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BulkAddModal({ targetIds, targets, campaigns, initialMode, onClose, onSuccess }) {
  const [mode, setMode] = useState(initialMode || 'existing'); // existing | new
  const [campagneId, setCampagneId] = useState('');
  const [newName, setNewName] = useState(`Lot ${targets[0]?.enseigne_group || 'Cibles'} (${targetIds.length})`);
  const [concurrent, setConcurrent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const totalCA = targets.reduce((s, t) => s + (t.ca_estime || 0), 0);
  const byGroup = {};
  targets.forEach(t => { byGroup[t.enseigne_group] = (byGroup[t.enseigne_group] || 0) + 1; });

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { target_ids: targetIds, skip_wasabi_matches: true };
      if (mode === 'existing') {
        if (!campagneId) { toast.error('Sélectionnez une campagne'); setSubmitting(false); return; }
        payload.campagne_id = campagneId;
      } else {
        if (!newName.trim()) { toast.error('Nom de campagne requis'); setSubmitting(false); return; }
        payload.nouvelle_campagne_nom = newName.trim();
        payload.nouvelle_campagne_concurrent = concurrent.trim();
      }
      const { data } = await api.post('/maps/targets/bulk-to-prospect', payload);
      setResult(data);
      toast.success(`${data.created} prospects créés${data.enrichment_queued ? ` • 🔍 ${data.enrichment_queued} enrichissements lancés` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-add-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
          {result ? 'Opération terminée' : 'Ajouter en lot'}
        </h3>
        <p className="text-xs text-[#71717A] mb-3">{targetIds.length} cible{targetIds.length > 1 ? 's' : ''} sélectionnée{targetIds.length > 1 ? 's' : ''}</p>

        {/* Summary */}
        <div className="bg-[#F9FAFB] rounded-lg p-3 mb-4 text-xs space-y-1">
          <div className="flex flex-wrap gap-2">
            {Object.entries(byGroup).map(([g, n]) => (
              <span key={g} className="px-2 py-0.5 rounded text-[11px] font-medium" style={{
                background: `${ENSEIGNE_COLORS[g]}15`, color: ENSEIGNE_COLORS[g]
              }}>{g} : {n}</span>
            ))}
          </div>
          <div className="font-semibold text-[#B45309] pt-1">💰 CA cumulé : {formatCA(totalCA)}</div>
        </div>

        {result ? (
          <div className="space-y-2">
            <div className="bg-[#ECFDF5] border border-[#10B981]/30 rounded-lg p-3 text-sm">
              <div className="font-semibold text-[#065F46]">✓ {result.created} prospects créés</div>
              {result.enrichment_queued > 0 && (
                <div className="text-[11px] text-[#059669] mt-1">🔍 Enrichissement auto lancé pour {result.enrichment_queued} prospects (~10-30s chacun)</div>
              )}
            </div>
            {(result.skipped_wasabi > 0 || result.skipped_duplicate > 0) && (
              <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-3 text-xs text-[#92400E]">
                {result.skipped_wasabi > 0 && <div>🛡 {result.skipped_wasabi} ignoré(s) : déjà clients Wasabi</div>}
                {result.skipped_duplicate > 0 && <div>🔄 {result.skipped_duplicate} ignoré(s) : prospect déjà existant</div>}
              </div>
            )}
            <Button onClick={onSuccess} className="w-full bg-[#F97316] hover:bg-[#EA580C] mt-2">Fermer</Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1 mb-3">
              {[['existing', 'Campagne existante'], ['new', 'Nouvelle campagne']].map(([k, l]) => (
                <button key={k} onClick={() => setMode(k)}
                  className={`py-2 px-2 rounded text-xs font-medium border ${mode === k ? 'bg-[#F97316] text-white border-[#F97316]' : 'bg-white text-[#71717A] border-[#E5E7EB] hover:bg-[#FFF7ED]'}`}>
                  {l}
                </button>
              ))}
            </div>
            {mode === 'existing' && (
              <Select value={campagneId} onValueChange={setCampagneId}>
                <SelectTrigger className="h-9 mb-4" data-testid="bulk-camp-select"><SelectValue placeholder="Sélectionner..."/></SelectTrigger>
                <SelectContent>
                  {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {mode === 'new' && (
              <>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nom de campagne" className="h-9 mb-2" data-testid="bulk-new-name"/>
                <Input value={concurrent} onChange={e => setConcurrent(e.target.value)} placeholder="Prestataire en place (optionnel)" className="h-9 mb-4" data-testid="bulk-new-concurrent"/>
              </>
            )}
            <div className="bg-[#EFF6FF] border border-[#0055FF]/20 rounded-lg p-2.5 mb-3 text-[11px] text-[#1E40AF]">
              ℹ Les cibles déjà clients Wasabi ou déjà en prospect seront ignorées automatiquement.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>Annuler</Button>
              <Button onClick={submit} className="flex-1 bg-[#F97316] hover:bg-[#EA580C]" disabled={submitting} data-testid="bulk-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : `Créer ${targetIds.length} prospects`}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

